// PRD 14 I5 (story 51) — Tenis10 Claims' activity log: every Claim, cancel, move, hide, show-again, send, resend, forward and
// new link (P55: and the family link sent to a new email, with both addresses), the newest first, with the note support wrote
// and who acted. A family's page shows its own (its id and its
// children's); the log page shows all of them and names the children and families from their ids (the log keeps ids only).
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { getClaimLogs, getProfiles } from "./api"
import { actorText, errorText, fieldsText, formatDateTime, legacyIdText, logActionText } from "./claimsLabels"
import { passportWindowText } from "./passportLabels"
import "../../assets/scss/custom/pages/_tenis10Claims.scss"

const LIMIT = 20

/** The log's lines for `ids` (`all`: every line), a page at a time, with the names of the children and families they cite. */
export function useClaimLogs(ids, { resolveNames = false, all = false } = {}) {
  const key = ids.join(",")
  const [page, setPage] = useState(1)
  const [state, setState] = useState({ status: "loading", logs: [], names: {} })
  // the latest page asked for: an older answer never shows under a newer page, nor after the page closed
  const request = useRef(0)
  useEffect(() => () => { request.current += 1 }, [])
  const load = useCallback(async (nextPage = 1) => {
    // no ids yet (a family still loading) reads nothing — only the log page asks for every line
    if (!key && !all) return
    const id = ++request.current
    setState(previous => ({ ...previous, status: "loading" }))
    try {
      const data = await getClaimLogs({ ids: key ? key.split(",") : [], page: nextPage, limit: LIMIT })
      let names = {}
      if (resolveNames) {
        const cited = [...new Set(data.logs.flatMap(entry => [entry.profileId, entry.invitationId]).filter(Boolean))]
        if (cited.length) {
          const found = await getProfiles(cited).catch(() => ({ profiles: [], families: [] }))
          names = Object.fromEntries([
            ...found.profiles.map(profile => [profile.profileId, { child: `${profile.firstName} ${profile.lastName}`, family: profile.family }]),
            ...found.families.map(family => [family.invitationId, { family }]),
          ])
        }
      }
      if (request.current !== id) return
      setPage(nextPage)
      setState({ status: "ready", logs: data.logs, total: data.total, totalPages: data.totalPages, names })
    } catch (error) {
      if (request.current !== id) return
      setState({ status: "error", logs: [], names: {}, message: errorText(error, "The activity log didn't load. Click Retry.") })
    }
  }, [key, resolveNames, all])
  useEffect(() => { load(1) }, [load])
  return { ...state, page, load }
}

const Context = ({ entry, names, childNames }) => {
  const child = childNames?.[entry.profileId] || names?.[entry.profileId]?.child
  const family = names?.[entry.profileId]?.family || names?.[entry.invitationId]?.family
  const details = entry.details || {}
  const items = [
    child,
    family && <Link key="family" to={`/accounts/tenis10-claims/families/${family.invitationId}`}>{legacyIdText(family.legacyUserId)}</Link>,
    details.receiverUserId && `→ #${details.receiverUserId}`,
    details.formerUserId && `was #${details.formerUserId}`,
    details.fromUserId && details.toUserId && `#${details.fromUserId} → #${details.toUserId}`,
    details.variant && `variant ${details.variant}`,
    details.step && `step ${details.step}`,
    // PRD 16 LR12 — the window of a passport support activated
    details.startDate && details.endDate && `passport ${passportWindowText(details.startDate, details.endDate)}`,
    // RELEASE-TENIS10 § 11 P5 — the fields support's correction of a child's data changed
    fieldsText(details.fields) && `changed: ${fieldsText(details.fields)}`,
    // RELEASE-TENIS10 § 11 P55 — where the family's emails went, and where they go now
    details.toEmail && (details.fromEmail ? `${details.fromEmail} → ${details.toEmail}` : `→ ${details.toEmail}`),
  ].filter(Boolean)
  return items.length ? items.map((item, index) => <span key={index} className="badge badge-soft-secondary mr-1 mb-1">{item}</span>) : "—"
}

/** The log's table: when, the action, its context, who acted, the note. */
export function ClaimsLogTable({ log, childNames = null, label = "Activity log" }) {
  if (log.status === "error") return <div className="tenis10-claims-state" role="alert"><p>{log.message}</p>
    <button type="button" className="btn btn-primary" onClick={() => log.load(log.page)}>Retry</button></div>
  if (log.status === "loading" && !log.logs.length) return <div className="tenis10-claims-state" role="status"><Spinner size="sm" className="mr-2" />Loading the activity log…</div>
  if (!log.logs.length) return <div className="tenis10-claims-state" role="status">Nothing recorded yet.</div>
  return <>
    <div className="table-responsive"><table className="table table-hover tenis10-claims-table mb-0" aria-label={label}>
      <thead className="thead-light"><tr><th scope="col">When</th><th scope="col">Action</th><th scope="col">Context</th><th scope="col">Performed by</th><th scope="col">Note</th></tr></thead>
      <tbody>{log.logs.map(entry => <tr key={entry.id}>
        <td>{formatDateTime(entry.at)}</td>
        <td data-label="Action" className="font-weight-medium">{logActionText(entry)}</td>
        <td data-label="Context"><Context entry={entry} names={log.names} childNames={childNames} /></td>
        <td data-label="Performed by">{actorText(entry.actor, entry.action)}</td>
        <td data-label="Note" className="tenis10-claims-note">{entry.note || "—"}</td>
      </tr>)}</tbody>
    </table></div>
    {log.totalPages > 1 && <div className="tenis10-claims-footer">
      <div role="status">{log.total} entries</div>
      <nav aria-label="Activity log pages" className="tenis10-claims-pager">
        <button type="button" aria-label="Newer entries" className="btn btn-light btn-sm" disabled={log.page <= 1} onClick={() => log.load(log.page - 1)}><i className="bx bx-chevron-left" aria-hidden="true" /></button>
        <span>Page {log.page} of {log.totalPages}</span>
        <button type="button" aria-label="Older entries" className="btn btn-light btn-sm" disabled={log.page >= log.totalPages} onClick={() => log.load(log.page + 1)}><i className="bx bx-chevron-right" aria-hidden="true" /></button>
      </nav>
    </div>}
  </>
}

const NO_IDS = []

/** The whole log (the I0 simulation's cp-log). */
export default function ClaimsLogPage() {
  const log = useClaimLogs(NO_IDS, { resolveNames: true, all: true })
  return <div className="page-content tenis10-claims"><Container fluid>
    <Breadcrumbs title="Tenis10 Claims" breadcrumbItem="Activity log" path="/accounts/tenis10-claims" />
    <Card><CardBody className="p-0">
      <div className="p-3 border-bottom">
        <h4 className="card-title mb-1">Activity log {log.status === "ready" && <span className="badge badge-soft-secondary">{log.total}</span>}</h4>
        <p className="card-title-desc mb-0">Every Tenis10 claim action, newest first. Support actions carry the note that was written.</p>
      </div>
      <ClaimsLogTable log={log} />
    </CardBody></Card>
  </Container></div>
}
